import { useState, useEffect, useCallback, useId } from 'react';
import { useApiClients } from '../../../context/ApiClientsContext';
import { useUserIdentityContext } from '../../../context/UserIdentityContext';
import { notificationPollingService } from '../../../services/NotificationPollingService';

export interface NotificationRecord {
  id: string;
  user_id: string;
  sphere_id?: string;
  center_id?: string;
  type: string;
  title: string;
  content: string;
  summary?: string;
  metadata?: any;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  read_at?: string;
}

export interface NotificationStats {
  total: number;
  pending: number;
  sent: number;
  read: number;
  failed: number;
}

export interface UseNotificationFeedOptions {
  itemsPerPage?: number;
  pollInterval?: number; // milliseconds
  autoMarkAsRead?: boolean;
}

export interface UseNotificationFeedReturn {
  notifications: NotificationRecord[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  unreadCount: number;
  hasMore: boolean;
  fetchNotifications: () => Promise<void>;
  fetchMore: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: (filters?: { type?: string; sphereId?: string }) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * useNotificationFeed - Hook for managing notification feed state
 * Handles fetching, pagination, marking as read, and real-time updates
 */
export const useNotificationFeed = (options: UseNotificationFeedOptions = {}): UseNotificationFeedReturn => {
  const { itemsPerPage = 20, pollInterval = 30000, autoMarkAsRead = false } = options;

  const { identityId: currentUserIdentityId } = useUserIdentityContext();
  const { notificationClient } = useApiClients();

  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  // Stable per-hook-instance key so two mounted feeds are distinct subscribers.
  const subscriberId = `useNotificationFeed:${useId()}`;

  // Stats are owned by `notificationPollingService`, not fetched here. This hook
  // used to poll `/stats` on its own timer alongside the service and ToolbarContext,
  // so a single page load issued the same request three times and then kept three
  // independent polling loops running against one endpoint.
  const fetchStats = useCallback(async () => {
    await notificationPollingService.refresh();
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (reset = true) => {
      if (!currentUserIdentityId || !notificationClient) return;

      if (reset) {
        setIsLoading(true);
        setOffset(0);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      try {
        const currentOffset = reset ? 0 : offset;
        const response = await notificationClient.get(`/api/notifications/${currentUserIdentityId}`, {
          params: {
            limit: itemsPerPage,
            offset: currentOffset,
          },
        });

        let fetchedNotifications: NotificationRecord[] = [];
        if (response.data?.success) {
          fetchedNotifications = response.data.data;
        } else if (Array.isArray(response.data)) {
          fetchedNotifications = response.data;
        }

        if (reset) {
          setNotifications(fetchedNotifications);
        } else {
          setNotifications((prev) => [...prev, ...fetchedNotifications]);
        }

        setHasMore(fetchedNotifications.length === itemsPerPage);
        setOffset(currentOffset + fetchedNotifications.length);
      } catch (error: any) {
        console.error('Failed to fetch notifications:', error);
        setError(error.message || 'Failed to load notifications');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [currentUserIdentityId, notificationClient, itemsPerPage, offset]
  );

  // Fetch more (for infinite scroll)
  const fetchMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    await fetchNotifications(false);
  }, [fetchNotifications, isLoadingMore, hasMore]);

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!currentUserIdentityId || !notificationClient) return;

      try {
        await notificationClient.patch(`/api/notifications/${notificationId}/read`, {
          userId: currentUserIdentityId,
        });

        // Optimistic update
        setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    },
    [currentUserIdentityId, notificationClient]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(
    async (filters?: { type?: string; sphereId?: string }) => {
      if (!currentUserIdentityId || !notificationClient) return;

      try {
        await notificationClient.patch(`/api/notifications/${currentUserIdentityId}/mark-all-read`, filters || {});

        // Optimistic update
        setNotifications((prev) =>
          prev.map((n) => {
            // Apply filters if provided
            if (filters?.type && n.type !== filters.type) return n;
            if (filters?.sphereId && n.sphere_id !== filters.sphereId) return n;

            // Mark as read if unread
            if (n.status !== 'read') {
              return { ...n, status: 'read' as const, read_at: new Date().toISOString() };
            }
            return n;
          })
        );

        // Refresh stats
        await fetchStats();
      } catch (error) {
        console.error('Failed to mark all notifications as read:', error);
      }
    },
    [currentUserIdentityId, notificationClient, fetchStats]
  );

  // Refresh feed
  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(true), fetchStats()]);
  }, [fetchNotifications, fetchStats]);

  // Initial load of the notification *list* (which is this hook's own concern).
  //
  // `fetchNotifications` is deliberately NOT a dependency: it closes over `offset`
  // and also sets it, so including it would give this effect a new identity on every
  // fetch and loop. The identity that decides when to reload the list is the user +
  // client pair, which is what is listed.
  useEffect(() => {
    if (currentUserIdentityId && notificationClient) {
      fetchNotifications(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserIdentityId, notificationClient]);

  // Unread count comes from the shared poller. Subscribing rather than polling is
  // what collapses the duplicate `/stats` requests; `pollInterval` is now a request
  // for a cadence, which the service reconciles against its other subscribers.
  useEffect(() => {
    if (!currentUserIdentityId || !notificationClient) return;

    notificationPollingService.subscribe(
      subscriberId,
      currentUserIdentityId,
      notificationClient,
      (_stats, unread) => setUnreadCount(unread),
      undefined,
      { pollIntervalMs: pollInterval }
    );

    return () => notificationPollingService.unsubscribe(subscriberId);
  }, [subscriberId, currentUserIdentityId, notificationClient, pollInterval]);

  return {
    notifications,
    isLoading,
    isLoadingMore,
    error,
    unreadCount,
    hasMore,
    fetchNotifications: () => fetchNotifications(true),
    fetchMore,
    markAsRead,
    markAllAsRead,
    refresh,
  };
};
